/**
 * LES TYPES DE CHAMP : le catalogue, et les helpers qui lisent et ecrivent leurs
 * valeurs (`docs/socle/champs-types.md`, sections 1 et 2).
 *
 * LA REGLE QUI TIENT TOUT : AUCUNE FORME DE VALEUR NEUVE. Une valeur du sac
 * `customFields` reste une chaine, un nombre, un booleen, une liste de chaines
 * ou `null` (`customFieldValueSchema`, `base.ts`). Une periode est la chaine
 * `AAAA-MM-JJ/AAAA-MM-JJ`, une liste de controle une liste `[x] texte`, une
 * duree un nombre de minutes, un numero la chaine deja formatee. Les regles,
 * le SDK, l'export, Lia et le MCP lisent donc les fiches comme avant.
 *
 * CE QUE CE MODULE FAIT POUR TOUS : ces formes sont simples a stocker et
 * penibles a recomposer a la main. Chaque helper ci-dessous est l'UNIQUE lecture
 * (ou ecriture) d'une forme : l'editeur de la fiche, le pied de tableau, le
 * serveur MCP et l'import passent par lui, et aucun ne reinvente le decoupage
 * d'une periode ou le compte d'une liste de controle. Tous sont PURS : ni
 * Firestore, ni React, ni horloge (une date se passe en argument).
 *
 * LE CATALOGUE lui-meme vit dans `catalogue-des-types.mjs`, en JavaScript pour
 * etre lu tel quel par les modules `.mjs` du socle et les scripts de la CI ; il
 * est reexporte ici avec ses types.
 */

import type { CustomFieldValue } from "./base.js";
import { arrondir } from "./formule.js";

export {
  CATALOGUE_DES_TYPES,
  IDS_TYPES_DE_CHAMP,
  REGLAGES_DE_CHAMP,
  accepteReglage,
  estAutomatique,
  estNumerique,
  estSommable,
  ficheDuType,
  genreDuType,
  libelleDuType,
  natureDuChamp,
  natureDuType,
  sansTypeFondu,
  TYPES_FONDUS,
} from "./catalogue-des-types.mjs";
export type { FicheDeType, NatureDeValeur, ReglageDeChamp } from "./catalogue-des-types.mjs";

/* -------------------------------------------------------------------------- */
/* Les defauts des reglages                                                    */
/* -------------------------------------------------------------------------- */

/** La devise d'un montant qui n'en declare pas : celle de l'habillage `euros` (code ISO 4217). */
export const DEVISE_PAR_DEFAUT = "EUR";
/** La note maximale d'un champ `note` qui n'en declare pas. */
export const NOTE_MAX_PAR_DEFAUT = 5;

/** Le reglage d'un numero automatique : son modele, et le nombre de chiffres du numero. */
export interface SequenceDeNumero {
  modele: string;
  chiffres?: number | undefined;
}

/**
 * LE NUMERO D'UN CHAMP QUI NE DECLARE PAS DE SEQUENCE : le numero nu (1, 2,
 * 3...). Decision du 27/10/2026 : `sequence` est FACULTATIVE. Un champ
 * « Numero automatique » ajoute au Studio doit numeroter tout de suite, sans
 * reglage a remplir avant ; le modele (`ASS-{annee}-{numero}`) et le
 * remplissage (`chiffres`) s'ajoutent quand on en veut.
 */
export const SEQUENCE_PAR_DEFAUT: SequenceDeNumero = Object.freeze({ modele: "{numero}" });

/* -------------------------------------------------------------------------- */
/* Les dates pures                                                             */
/* -------------------------------------------------------------------------- */

const JOUR_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Les composantes d'un jour `AAAA-MM-JJ` qui existe au calendrier, ou `null`. */
function composantes(jour: string): { annee: number; mois: number; jour: number } | null {
  const lu = JOUR_ISO.exec(jour);
  if (lu === null) return null;
  const [annee, mois, quantieme] = [Number(lu[1]), Number(lu[2]), Number(lu[3])];
  const date = new Date(Date.UTC(annee, mois - 1, quantieme));
  if (date.getUTCFullYear() !== annee || date.getUTCMonth() !== mois - 1 || date.getUTCDate() !== quantieme) return null;
  return { annee, mois, jour: quantieme };
}

/** Le jour `AAAA-MM-JJ` d'une date UTC (le calcul de calendrier se fait en UTC, sans fuseau). */
const jourDe = (date: Date): string => date.toISOString().slice(0, 10);

/* -------------------------------------------------------------------------- */
/* La periode                                                                  */
/* -------------------------------------------------------------------------- */

/** Une periode lue : deux jours, le debut avant ou egal a la fin. */
export interface Periode {
  debut: string;
  fin: string;
}

/**
 * LIRE UNE PERIODE STOCKEE (`2026-11-05/2027-11-04`, intervalle ISO 8601 de deux
 * dates pures). `null` pour tout le reste : vide, autre forme, jour qui
 * n'existe pas, fin avant le debut.
 */
export function lirePeriode(valeur: unknown): Periode | null {
  if (typeof valeur !== "string") return null;
  const morceaux = valeur.split("/");
  if (morceaux.length !== 2) return null;
  const [debut, fin] = morceaux as [string, string];
  if (composantes(debut) === null || composantes(fin) === null || fin < debut) return null;
  return { debut, fin };
}

/**
 * ECRIRE UNE PERIODE dans sa forme stockee. Refuse (RangeError) un jour qui
 * n'est pas `AAAA-MM-JJ` ou une fin avant le debut : une periode a l'envers ne
 * se trie plus par son debut, et c'est ce tri qui justifie la forme.
 */
export function ecrirePeriode(debut: string, fin: string): string {
  if (composantes(debut) === null) throw new RangeError(`le début "${debut}" n'est pas un jour AAAA-MM-JJ`);
  if (composantes(fin) === null) throw new RangeError(`la fin "${fin}" n'est pas un jour AAAA-MM-JJ`);
  if (fin < debut) throw new RangeError(`la fin (${fin}) précède le début (${debut})`);
  return `${debut}/${fin}`;
}

const enPeriode = (periode: string | Periode): Periode | null => (typeof periode === "string" ? lirePeriode(periode) : lirePeriode(`${periode.debut}/${periode.fin}`));

/**
 * LE NOMBRE DE JOURS D'UNE PERIODE, bornes COMPRISES : du 5 au 5, un jour ; du
 * 5/11/2026 au 4/11/2027, 365 jours. `null` pour une periode illisible.
 */
export function dureeDePeriodeEnJours(periode: string | Periode): number | null {
  const lue = enPeriode(periode);
  if (lue === null) return null;
  const ecart = Date.parse(`${lue.fin}T00:00:00Z`) - Date.parse(`${lue.debut}T00:00:00Z`);
  return Math.round(ecart / 86_400_000) + 1;
}

/** Le jour qui suit `k` mois pleins depuis `debut`, moins un jour (le dernier jour de ces `k` mois). */
function finDeMoisPleins(debut: { annee: number; mois: number; jour: number }, k: number): string {
  const cible = new Date(Date.UTC(debut.annee, debut.mois - 1 + k, 1));
  const dernier = new Date(Date.UTC(cible.getUTCFullYear(), cible.getUTCMonth() + 1, 0)).getUTCDate();
  const lendemain = new Date(Date.UTC(cible.getUTCFullYear(), cible.getUTCMonth(), Math.min(debut.jour, dernier)));
  return jourDe(new Date(lendemain.getTime() - 86_400_000));
}

/**
 * LA DUREE D'UNE PERIODE EN MOTS, celle de la maquette (« 05/11/2026 ->
 * 04/11/2027 · 1 an ») : en annees quand elle tombe sur des annees pleines, en
 * mois quand elle tombe sur des mois pleins, en jours sinon (« 12 jours »,
 * « 1 jour »). `null` pour une periode illisible.
 */
export function libelleDureeDePeriode(periode: string | Periode): string | null {
  const lue = enPeriode(periode);
  if (lue === null) return null;
  const debut = composantes(lue.debut)!;
  const fin = composantes(lue.fin)!;
  const moisEcoules = (fin.annee - debut.annee) * 12 + (fin.mois - debut.mois);
  for (const k of [moisEcoules, moisEcoules + 1]) {
    if (k < 1 || finDeMoisPleins(debut, k) !== lue.fin) continue;
    if (k % 12 === 0) return k === 12 ? "1 an" : `${k / 12} ans`;
    return `${k} mois`;
  }
  const jours = dureeDePeriodeEnJours(lue)!;
  return jours === 1 ? "1 jour" : `${jours} jours`;
}

/* -------------------------------------------------------------------------- */
/* La liste de controle                                                        */
/* -------------------------------------------------------------------------- */

/** Un element d'une liste de controle. */
export interface ElementDeChecklist {
  texte: string;
  fait: boolean;
}

const LIGNE_DE_CHECKLIST = /^\s*(?:[-*]\s+)?\[( |x|X)\]\s?(.*)$/;
const texteDElement = (texte: string): string => texte.replace(/\s+/g, " ").trim();

/**
 * LIRE UNE LISTE DE CONTROLE : la liste stockee (`["[x] Attestation recue",
 * "[ ] Contrat signe"]`), ou un texte d'une ligne par element (une saisie, un
 * import, un Markdown `- [x] ...`). Une ligne sans case est un element a faire ;
 * une ligne vide n'est pas un element.
 */
export function lireChecklist(valeur: unknown): ElementDeChecklist[] {
  const lignes = typeof valeur === "string" ? valeur.split(/\r?\n/) : Array.isArray(valeur) ? valeur.filter((l): l is string => typeof l === "string") : [];
  const elements: ElementDeChecklist[] = [];
  for (const ligne of lignes) {
    const lue = LIGNE_DE_CHECKLIST.exec(ligne);
    const texte = texteDElement(lue === null ? ligne : lue[2]!);
    if (texte !== "") elements.push({ texte, fait: lue !== null && lue[1] !== " " });
  }
  return elements;
}

/** ECRIRE UNE LISTE DE CONTROLE dans sa forme stockee : `[x] texte` ou `[ ] texte`, sans element vide. */
export function ecrireChecklist(elements: readonly ElementDeChecklist[]): string[] {
  return elements
    .map((element) => ({ texte: texteDElement(element.texte), fait: element.fait }))
    .filter((element) => element.texte !== "")
    .map((element) => `${element.fait ? "[x]" : "[ ]"} ${element.texte}`);
}

/** OU EN EST UNE LISTE DE CONTROLE : « 2 sur 3 » se lit `{ faits: 2, total: 3 }`. */
export function avancementChecklist(valeur: unknown): { faits: number; total: number } {
  const elements = Array.isArray(valeur) && valeur.every((e) => typeof e === "object" && e !== null && "texte" in e)
    ? (valeur as ElementDeChecklist[])
    : lireChecklist(valeur);
  return { faits: elements.filter((element) => element.fait).length, total: elements.length };
}

/* -------------------------------------------------------------------------- */
/* La duree                                                                    */
/* -------------------------------------------------------------------------- */

const DUREE_HEURES_DEUX_POINTS = /^(\d+):([0-5]\d)$/;
const DUREE_HEURES = /^(\d+(?:\.\d+)?)h(?:(\d+)(?:min|mn|m)?)?$/;
const DUREE_MINUTES = /^(\d+)(?:min|mn|m)$/;
const DUREE_NUE = /^\d+$/;

/**
 * UNE DUREE SAISIE EN MINUTES ENTIERES, la forme stockee. Accepte « 2 h 30 »,
 * « 2h30 », « 2:30 », « 1,5 h », « 45 min », « 2 heures » et un nombre nu, lu
 * en MINUTES (« 150 »). `null` pour le reste, et pour un nombre nu a virgule
 * (« 2,5 » : des heures ou des minutes ? on ne devine pas). Un nombre (pas un
 * texte) est deja des minutes, arrondi a la minute.
 */
export function lireDuree(saisie: string | number): number | null {
  if (typeof saisie === "number") return Number.isFinite(saisie) && saisie >= 0 ? Math.round(saisie) : null;
  if (typeof saisie !== "string") return null;
  const texte = saisie
    .toLowerCase()
    .replace(/heures?/g, "h")
    .replace(/minutes?/g, "min")
    .replace(/\s+/g, "")
    .replace(",", ".");
  if (texte === "") return null;
  let lu = DUREE_HEURES_DEUX_POINTS.exec(texte);
  if (lu !== null) return Number(lu[1]) * 60 + Number(lu[2]);
  lu = DUREE_HEURES.exec(texte);
  if (lu !== null) {
    const minutes = lu[2] === undefined ? 0 : Number(lu[2]);
    if (minutes >= 60 || (lu[2] !== undefined && lu[1]!.includes("."))) return null;
    return Math.round(Number(lu[1]) * 60) + minutes;
  }
  lu = DUREE_MINUTES.exec(texte);
  if (lu !== null) return Number(lu[1]);
  return DUREE_NUE.test(texte) ? Number(texte) : null;
}

/** UNE DUREE EN MINUTES, ECRITE COMME LA MAQUETTE : « 2 h 30 », « 2 h », « 45 min », « 0 min ». */
export function formaterDuree(minutes: number): string {
  const total = Math.round(Math.abs(minutes));
  const signe = minutes < 0 && total > 0 ? "-" : "";
  const heures = Math.floor(total / 60);
  const reste = total % 60;
  if (heures === 0) return `${signe}${reste} min`;
  return reste === 0 ? `${signe}${heures} h` : `${signe}${heures} h ${String(reste).padStart(2, "0")}`;
}

/* -------------------------------------------------------------------------- */
/* Le numero automatique                                                       */
/* -------------------------------------------------------------------------- */

/** Le modele contient-il la place du numero ? `{numero}` ou `{numéro}`, en toutes lettres. */
export function modeleDeNumeroValide(modele: string): boolean {
  const nfc = modele.normalize("NFC");
  return nfc.includes("{numero}") || nfc.includes("{numéro}");
}

/**
 * LE NUMERO FORMATE, tel qu'il se stocke : `{numero}` (ou `{numéro}`) devient
 * `n`, complete de zeros a gauche jusqu'a `chiffres` ; `{annee}` (ou `{année}`)
 * devient l'annee de `date`. `date` est un jour `AAAA-MM-JJ` (celui de l'org,
 * `jourDansFuseau`, pour qu'un numero du 31 decembre au soir porte la bonne
 * annee) ou un instant, lu en UTC. Sans sequence : le numero nu.
 */
export function formaterNumero(sequence: SequenceDeNumero | undefined, n: number, date: Date | string): string {
  const { modele, chiffres } = sequence ?? SEQUENCE_PAR_DEFAUT;
  const numero = String(Math.max(0, Math.trunc(n))).padStart(chiffres ?? 1, "0");
  const annee = typeof date === "string" ? date.slice(0, 4) : String(date.getUTCFullYear());
  return modele
    .normalize("NFC")
    .split("{numéro}").join(numero)
    .split("{numero}").join(numero)
    .split("{année}").join(annee)
    .split("{annee}").join(annee);
}

/** La collection de l'etat des sequences d'une org : `orgs/{orgId}/sequences`. */
export const COLLECTION_DES_SEQUENCES = "sequences";

/**
 * L'ID DU DOCUMENT DE SEQUENCE d'un champ `numero` :
 * `<family>~<type ou _>~<clef>`. Un jeu et une clef, donc une sequence par
 * champ, partagee par toutes les apps qui montrent ce jeu (le numero est un
 * fait de la fiche, pas de l'ecran). `~` n'apparait ni dans une famille, ni
 * dans un type, ni dans une clef : l'id se relit sans ambiguite.
 */
export function cleDeSequence(cible: { family: string; type?: string | undefined; key: string }): string {
  return `${cible.family}~${cible.type ?? "_"}~${cible.key}`;
}

/** L'etat d'une sequence : le prochain numero a donner, le dernier donne (`null` : aucun). */
export interface EtatDeSequence {
  prochain: number;
  dernier: number | null;
}

/* -------------------------------------------------------------------------- */
/* La somme au centime                                                         */
/* -------------------------------------------------------------------------- */

/**
 * LA SOMME EXACTE DE MONTANTS. Additionner des flottants fait 0,1 + 0,2 =
 * 0,30000000000000004, et un pied de colonne de mille lignes derive d'un
 * centime. On additionne des CENTIMES entiers, puis on revient aux unites. Les
 * valeurs vides ou non numeriques sont ignorees.
 */
export function sommeAuCentime(nombres: readonly unknown[]): number {
  let centimes = 0;
  for (const nombre of nombres) {
    if (typeof nombre !== "number" || !Number.isFinite(nombre)) continue;
    centimes += Math.round(arrondir(nombre, 2) * 100);
  }
  const somme = centimes / 100;
  return Object.is(somme, -0) ? 0 : somme;
}

/* -------------------------------------------------------------------------- */
/* Le format d'un nombre                                                       */
/* -------------------------------------------------------------------------- */

/**
 * LE FORMAT D'UN NOMBRE : comment il s'ECRIT et se SAISIT, jamais ce qu'il
 * vaut. La valeur stockee reste le nombre nu, et une formule, un tri, un
 * filtre ou un export la lisent telle quelle.
 * - `montant` : en unites de sa devise (12.5 vaut « 12,50 € »), saisi
 *   « 1 250,50 € », arrondi et somme au centime ;
 * - `pourcentage` : de 0 a 100 (65 vaut « 65 % »), saisi « 65 % » ;
 * - `duree` : en MINUTES (150 vaut « 2 h 30 »), saisi « 2 h 30 », « 2:30 » ;
 * - `taux` : une FRACTION (0,21 vaut « 21 % »), celle que stocke un natif comme
 *   `taxRate` d'un produit ; saisi « 21 % », « 21 » ou « 0,21 » (`lireTaux`).
 *   A ne pas confondre avec `pourcentage`, qui stocke 65 pour « 65 % ».
 */
export type FormatDeNombre = "montant" | "pourcentage" | "duree" | "taux";

/**
 * LES HABILLAGES DE COLONNE QUI DONNENT UN FORMAT A UN NOMBRE (`habillage` d'une
 * colonne de `taki.screens.json`, `HABILLAGES_SOCLE` de `@taki/ui`). C'est ainsi
 * que l'existant code un prix, un avancement ou un temps alloue : un `number`
 * et un habillage. Ces declarations ne changent pas ; elles se lisent ici.
 */
export const FORMAT_PAR_HABILLAGE: Readonly<Record<string, FormatDeNombre>> = Object.freeze({
  euros: "montant",
  pourcentage: "pourcentage",
  duree: "duree",
  taux: "taux",
});

/** Un champ, reduit a ce que son format lit. */
export interface ChampAFormat {
  type?: string | undefined;
  devise?: string | undefined;
  affichage?: string | undefined;
}

/**
 * LE FORMAT D'UN NOMBRE, d'un champ et de l'habillage de sa colonne : LA regle,
 * que lisent la case de fiche, le pied de colonne, le PDF, le fil de suivi
 * (qui n'a que le champ declare) et la saisie libre (`valeurNormalisee`).
 * Dans l'ordre :
 * 1. une `devise` declaree fait un MONTANT (sur un `number` ou un `calcul`) ;
 * 2. l'`affichage: 'jauge'` d'un `number` fait un POURCENTAGE ;
 * 3. sinon l'habillage de la colonne (`FORMAT_PAR_HABILLAGE`) : `euros`,
 *    `pourcentage`, `duree`, `taux`.
 * `null` : un nombre sans format (ecrit a la francaise, avec son unite), ou un
 * champ qui n'est pas un nombre. Un champ sans type connu (une colonne native)
 * ne se juge que sur son habillage.
 */
export function formatDuNombre(champ: ChampAFormat, habillage?: string): FormatDeNombre | null {
  if (champ.type !== undefined && champ.type !== "number" && champ.type !== "calcul") return null;
  if (champ.devise !== undefined) return "montant";
  if (champ.type === "number" && champ.affichage === "jauge") return "pourcentage";
  return habillage !== undefined && Object.hasOwn(FORMAT_PAR_HABILLAGE, habillage) ? FORMAT_PAR_HABILLAGE[habillage]! : null;
}

/** CE NOMBRE EST-IL UN MONTANT ? Un `number` ou un `calcul` qui porte `devise`, ou une colonne habillee `euros`. */
export function estMonetaire(champ: ChampAFormat, habillage?: string): boolean {
  return formatDuNombre(champ, habillage) === "montant";
}

/**
 * LA DEVISE D'UN MONTANT : celle qu'il declare, `EUR` pour une colonne habillee
 * `euros` qui n'en declare pas ; `null` pour un nombre qui n'est pas un montant.
 */
export function deviseDe(champ: ChampAFormat, habillage?: string): string | null {
  if (!estMonetaire(champ, habillage)) return null;
  return champ.devise ?? DEVISE_PAR_DEFAUT;
}

/**
 * CE CHAMP SE SOMME-T-IL AU CENTIME ? Quand c'est un MONTANT (`estMonetaire`) :
 * son total passe par `sommeAuCentime`. Un nombre ou une duree se somment tels
 * quels : arrondir 0,125 kg au centime serait une faute. LA regle, lue par le
 * pied de colonne (`@taki/ui`, avec l'habillage de la colonne) et par le total
 * lie (`@taki/app-runtime`, sur le champ declare).
 */
export function seSommeAuCentime(champ: ChampAFormat, habillage?: string): boolean {
  return estMonetaire(champ, habillage);
}

/* -------------------------------------------------------------------------- */
/* Les bornes                                                                  */
/* -------------------------------------------------------------------------- */

/** UNE NOTE DANS SES BORNES : un entier de 0 a `max`. `null` pour ce qui n'est pas un nombre. */
export function noteBornee(valeur: unknown, max: number = NOTE_MAX_PAR_DEFAUT): number | null {
  if (typeof valeur !== "number" || !Number.isFinite(valeur)) return null;
  return Math.min(max, Math.max(0, Math.round(valeur)));
}

/** UN POURCENTAGE DANS SES BORNES : de 0 a 100 (la jauge). `null` pour ce qui n'est pas un nombre. */
export function pourcentageBorne(valeur: unknown): number | null {
  if (typeof valeur !== "number" || !Number.isFinite(valeur)) return null;
  return Math.min(100, Math.max(0, valeur));
}

/* -------------------------------------------------------------------------- */
/* La saisie libre ramenee a la forme stockee                                  */
/* -------------------------------------------------------------------------- */

/** Un champ, reduit a ce que la normalisation d'une valeur lit. */
export interface ChampANormaliser {
  key: string;
  label?: string | undefined;
  type: string;
  options?: readonly { value: string; label: string; archived?: boolean | undefined }[] | undefined;
  max?: number | undefined;
  /** Le format d'un nombre (`formatDuNombre`) : une devise en fait un montant. */
  devise?: string | undefined;
  /** `jauge` : un pourcentage de 0 a 100. */
  affichage?: string | undefined;
  /** Un `lien` qui porte `plusieurs` prend une liste d'ids. */
  plusieurs?: boolean | undefined;
}

/** Une valeur ramenee a sa forme stockee, ou pourquoi elle est refusee. */
export type ValeurNormalisee =
  | { valeur: CustomFieldValue }
  | { refus: string; valeursPermises?: string[]; libellesPermis?: string[] };

const ESPACES_DE_NOMBRE = /[\s  ']/g;
const SYMBOLES_MONETAIRES = /€|\$|£|\b[A-Z]{3}\b/g;

/**
 * UN NOMBRE ECRIT PAR UN HUMAIN : « 1 250,50 », « 1.250,50 », « 1,250.50 »,
 * « 4,5 », « 12 € ». Quand le point et la virgule sont tous deux la, le DERNIER
 * des deux est la marque decimale et l'autre separe les milliers.
 */
function nombreEcrit(texte: string, retirer: RegExp | null = null): number | null {
  let t = texte.trim();
  if (retirer !== null) t = t.replace(retirer, "");
  t = t.replace(ESPACES_DE_NOMBRE, "");
  const virgule = t.lastIndexOf(",");
  const point = t.lastIndexOf(".");
  if (virgule >= 0 && point >= 0) t = virgule > point ? t.replace(/\./g, "").replace(",", ".") : t.replace(/,/g, "");
  else if (virgule >= 0) t = t.replace(",", ".");
  return /^[-+]?\d+(?:\.\d+)?$/.test(t) ? Number(t) : null;
}

const nombreOuTexte = (brut: unknown, retirer: RegExp | null = null): number | null =>
  typeof brut === "number" ? (Number.isFinite(brut) ? brut : null) : typeof brut === "string" ? nombreEcrit(brut, retirer) : null;

/**
 * UN TAUX LU DE SA SAISIE, en FRACTION (0,21 pour 21 %), la forme stockee d'un
 * `taxRate`. La regle, nette :
 * - avec un signe « % », la saisie est un POURCENTAGE : « 21 % » et « 0,5 % »
 *   valent 0,21 et 0,005 ;
 * - sans signe, un nombre dont la valeur absolue tient dans [0 ; 1] est DEJA une
 *   fraction (« 0,21 » vaut 0,21, « 1 » vaut 100 %), et un nombre plus grand est
 *   un pourcentage (« 21 » vaut 0,21, « 150 » vaut 1,5). Un taux de moins de
 *   1 % s'ecrit donc avec son signe (« 0,5 % »).
 * Un nombre (pas un texte) suit la meme regle. `null` pour ce qui n'est pas un
 * nombre. Le resultat perd son bruit binaire (21 / 100 est 0,21, pas
 * 0,21000000000000002).
 */
export function lireTaux(brut: unknown): number | null {
  const nombre = nombreOuTexte(brut, /%/g);
  if (nombre === null) return null;
  const avecSigne = typeof brut === "string" && brut.includes("%");
  return arrondir(avecSigne || Math.abs(nombre) > 1 ? nombre / 100 : nombre, 10);
}

/** UN TAUX (fraction) EN POURCENTS, sans le bruit binaire : 0,21 donne 21, 0,055 donne 5,5. */
export function tauxEnPourcents(fraction: number): number {
  return arrondir(fraction * 100, 8);
}

const compact = (texte: string): string => texte.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, "");

/** Une liste de chaines non vides, sans doublon : depuis une liste, ou un texte coupe par `,` ou `;`. */
function listeDeTextes(brut: unknown, couper: boolean): string[] | null {
  const morceaux = Array.isArray(brut) ? brut : typeof brut === "string" ? (couper ? brut.split(/[,;\n]/) : [brut]) : null;
  if (morceaux === null || !morceaux.every((m) => typeof m === "string")) return null;
  const vus: string[] = [];
  for (const morceau of morceaux as string[]) {
    const texte = morceau.trim();
    if (texte !== "" && !vus.includes(texte)) vus.push(texte);
  }
  return vus;
}

const NOTE_ECRITE = /^\s*(\d+)\s*(?:\/\s*(\d+))?\s*(?:[ée]toiles?)?\s*$/i;
const JOUR_FRANCAIS =/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const JOURS_DANS_UN_TEXTE = /\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}/g;
const INSTANT_AVEC_FUSEAU = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * UN INSTANT ECRIT DANS LE SAC, LU COMME L'INSTANT QU'IL EST (`docs/socle/datetime-sac.md`).
 *
 * `customFields` n'accepte pas d'objet `Date` : un champ `datetime` du sac est
 * donc rangé en TEXTE ISO (`2026-10-16T08:10:00.000Z`), alors que le champ natif
 * d'une famille porte une `Date`. Cette fonction est la regle UNIQUE qui
 * ramene le premier au second a la lecture, pour que l'ecran, le PDF et le fil
 * de suivi montrent la meme date-heure quel que soit l'etage ou elle est rangee.
 *
 * Une chaine ISO d'instant COMPLET (jour, heure, fuseau) rend la `Date`
 * correspondante ; tout le reste rend `null` (une date pure `AAAA-MM-JJ`, un
 * texte libre, un nombre, une chaine sans fuseau) : aucune date n'est inventee.
 */
export function instantDeSac(valeur: unknown): Date | null {
  if (typeof valeur !== "string") return null;
  const texte = valeur.trim();
  if (!INSTANT_AVEC_FUSEAU.test(texte)) return null;
  const instant = new Date(texte);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/**
 * LA VALEUR D'UNE COLONNE, LUE SELON SON TYPE : pour un `datetime`, l'instant du
 * sac devient une `Date` (`instantDeSac`) ; une chaine qui n'en est pas un, une
 * `Date` deja native, une valeur vide et tout autre type sortent TELS QUELS.
 * La date pure (`date`) ne change pas.
 */
export function valeurLueSelonLeType<T>(type: string | undefined, valeur: T): T | Date {
  if (type !== "datetime") return valeur;
  return instantDeSac(valeur) ?? valeur;
}

/** Un jour `AAAA-MM-JJ` lu d'une saisie (`2026-11-05`, `2026-11-05T...`, `05/11/2026`), ou `null`. */
function jourEcrit(texte: string): string | null {
  const t = texte.trim();
  const francais = JOUR_FRANCAIS.exec(t);
  const jour = francais !== null ? `${francais[3]}-${francais[2]!.padStart(2, "0")}-${francais[1]!.padStart(2, "0")}` : t.slice(0, 10);
  if (francais === null && !/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(t)) return null;
  return composantes(jour) === null ? null : jour;
}

/**
 * UNE SAISIE LIBRE RAMENEE A LA FORME STOCKEE DU TYPE, ou pourquoi elle est
 * refusee. C'est la porte d'entree de toute valeur qui ne vient pas d'un
 * editeur de la fiche : un agent MCP qui ecrit « 2 h 30 », un import qui porte
 * « 1 250,50 € », Lia qui donne une periode « du 05/11/2026 au 04/11/2027 ».
 * Le refus est une phrase qui montre la forme attendue, pour que l'auteur
 * corrige en un essai ; pour une liste, il porte aussi les valeurs permises.
 *
 * `null` (ou `undefined`) efface toujours. Un champ `calcul`, `numero` ou `lecture` ne se
 * saisit pas : sa valeur est posee par le socle. Une option archivee reste
 * acceptee quand c'est deja la valeur de la fiche (`precedente`).
 *
 * UN NOMBRE SE LIT SELON SON FORMAT (`formatDuNombre`, du champ et de
 * l'`habillage` de sa colonne quand l'appelant l'a) : un montant accepte
 * « 1 250,50 € » et s'arrondit au centime, un pourcentage accepte « 65 % »
 * (de 0 a 100 s'il s'affiche en jauge), une duree accepte « 2 h 30 » et se
 * stocke en minutes, un taux accepte « 21 % », « 21 » ou « 0,21 » et se stocke
 * en fraction (`lireTaux`). Sans format, un nombre a la francaise.
 */
export function valeurNormalisee(champ: ChampANormaliser, brut: unknown, precedente?: unknown, habillage?: string): ValeurNormalisee {
  if (brut === null || brut === undefined) return { valeur: null };
  const refus = (attendu: string): ValeurNormalisee => ({ refus: attendu });
  switch (champ.type) {
    case "calcul":
      return refus("aucune valeur : un calcul se recalcule seul à partir de sa formule");
    case "numero":
      return refus("aucune valeur : un numéro automatique est attribué par le socle");
    case "lecture":
      return refus("aucune valeur : une lecture montre un champ de la fiche liée, elle ne se saisit pas");
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "url":
      if (typeof brut === "string") return { valeur: brut };
      if (typeof brut === "number" && Number.isFinite(brut)) return { valeur: String(brut) };
      return refus("un texte");
    case "lien":
      if (champ.plusieurs === true) {
        const ids = listeDeTextes(brut, false);
        return ids === null ? refus("une liste d'identifiants de fiches") : { valeur: ids };
      }
      return typeof brut === "string" && brut.trim() !== "" ? { valeur: brut.trim() } : refus("l'identifiant d'une fiche");
    case "personne":
      return typeof brut === "string" && brut.trim() !== "" ? { valeur: brut.trim() } : refus("l'identifiant d'une fiche");
    case "number": {
      switch (formatDuNombre(champ, habillage)) {
        case "montant": {
          const nombre = nombreOuTexte(brut, SYMBOLES_MONETAIRES);
          return nombre === null ? refus("un montant, par exemple 1250 ou 1 250,50") : { valeur: arrondir(nombre, 2) };
        }
        case "pourcentage": {
          const nombre = nombreOuTexte(brut, /%/g);
          if (nombre === null) return refus("un pourcentage, par exemple 65");
          return champ.affichage === "jauge" && (nombre < 0 || nombre > 100) ? refus(`un pourcentage de 0 à 100 (reçu ${nombre})`) : { valeur: nombre };
        }
        case "duree": {
          const minutes = typeof brut === "number" || typeof brut === "string" ? lireDuree(brut) : null;
          return minutes === null ? refus("une durée, par exemple 2 h 30, 2:30, 45 min ou 150 (minutes)") : { valeur: minutes };
        }
        case "taux": {
          const fraction = lireTaux(brut);
          return fraction === null ? refus("un taux, par exemple 21 %, 21 ou 0,21 (stocké 0,21)") : { valeur: fraction };
        }
        default: {
          const nombre = nombreOuTexte(brut);
          return nombre === null ? refus("un nombre, par exemple 12 ou 4,5") : { valeur: nombre };
        }
      }
    }
    case "note": {
      const max = champ.max ?? NOTE_MAX_PAR_DEFAUT;
      /* « 4 », « 4/5 », « 4 etoiles » : un denominateur ecrit doit etre le max du champ, sinon 4/10 deviendrait 4/5. */
      const lue = typeof brut === "string" ? NOTE_ECRITE.exec(brut) : null;
      const nombre = typeof brut === "number" ? brut : lue !== null && (lue[2] === undefined || Number(lue[2]) === max) ? Number(lue[1]) : null;
      if (nombre === null || !Number.isInteger(nombre) || nombre < 0 || nombre > max) return refus(`une note entière de 0 à ${max}`);
      return { valeur: nombre };
    }
    case "boolean": {
      if (typeof brut === "boolean") return { valeur: brut };
      const mot = typeof brut === "string" ? compact(brut) : typeof brut === "number" ? String(brut) : "";
      if (["true", "oui", "vrai", "1"].includes(mot)) return { valeur: true };
      if (["false", "non", "faux", "0"].includes(mot)) return { valeur: false };
      return refus("true ou false");
    }
    case "date": {
      const jour = typeof brut === "string" ? jourEcrit(brut) : null;
      return jour === null ? refus("une date AAAA-MM-JJ, par exemple 2026-10-01") : { valeur: jour };
    }
    case "datetime": {
      if (brut instanceof Date && !Number.isNaN(brut.getTime())) return { valeur: brut.toISOString() };
      if (typeof brut === "string" && INSTANT_AVEC_FUSEAU.test(brut.trim()) && !Number.isNaN(Date.parse(brut))) {
        return { valeur: new Date(brut).toISOString() };
      }
      return refus("un instant avec son fuseau, par exemple 2026-11-05T14:30:00Z ou 2026-11-05T14:30:00+01:00");
    }
    case "periode": {
      const attendu = "une période AAAA-MM-JJ/AAAA-MM-JJ, par exemple 2026-11-05/2027-11-04";
      if (typeof brut !== "string") return refus(attendu);
      const jours = brut.match(JOURS_DANS_UN_TEXTE)?.map(jourEcrit) ?? [];
      if (jours.length !== 2 || jours.some((jour) => jour === null)) return refus(attendu);
      const [debut, fin] = jours as [string, string];
      return fin < debut ? refus(`${attendu} (la fin ${fin} précède le début ${debut})`) : { valeur: `${debut}/${fin}` };
    }
    case "checklist": {
      if (typeof brut !== "string" && !(Array.isArray(brut) && brut.every((ligne) => typeof ligne === "string"))) {
        return refus("une liste d'éléments, chacun « [x] texte » (fait) ou « [ ] texte » (à faire)");
      }
      return { valeur: ecrireChecklist(lireChecklist(brut)) };
    }
    case "etiquettes": {
      const etiquettes = listeDeTextes(brut, true);
      return etiquettes === null ? refus("une liste d'étiquettes, par exemple [\"Urgent\", \"Bail commercial\"]") : { valeur: etiquettes };
    }
    case "select":
    case "multiselect": {
      const options = champ.options ?? [];
      const ouvertes = options.filter((option) => option.archived !== true);
      const permis = { valeursPermises: ouvertes.map((option) => option.value), libellesPermis: ouvertes.map((option) => option.label) };
      const trouver = (item: unknown) => typeof item !== "string" ? undefined
        : options.find((option) => option.value === item) ?? options.find((option) => compact(option.value) === compact(item) || compact(option.label) === compact(item));
      const utilisable = (option: (typeof options)[number] | undefined): option is (typeof options)[number] =>
        option !== undefined && (option.archived !== true || (Array.isArray(precedente) ? precedente.includes(option.value) : precedente === option.value));
      if (champ.type === "select") {
        const option = trouver(brut);
        return utilisable(option) ? { valeur: option.value } : { refus: "une des valeurs de la liste", ...permis };
      }
      const trouvees = (Array.isArray(brut) ? brut : [brut]).map(trouver);
      return trouvees.every(utilisable) ? { valeur: [...new Set(trouvees.map((option) => option.value))] } : { refus: "une liste de valeurs de la liste", ...permis };
    }
    default: {
      const formeConnue = typeof brut === "string" || typeof brut === "boolean" || (typeof brut === "number" && Number.isFinite(brut))
        || (Array.isArray(brut) && brut.every((item) => typeof item === "string"));
      return formeConnue ? { valeur: brut as CustomFieldValue } : refus("un texte, un nombre, un booléen ou une liste de textes");
    }
  }
}
